import React, { useState, useCallback, useEffect } from 'react';
import { Box, Paper, Button, CircularProgress, Tabs, Tab } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { DesignerToolbar } from './DesignerToolbar';
import { DesignerCanvas } from './DesignerCanvas';
import { LayersPanel } from './LayersPanel';
import { ElementProperties } from './ElementProperties';
import { GlobalSettings } from './GlobalSettings';
import { CouponDesign, DesignElement, GridSettings, ElementType, Breakpoint } from '../../../types/coupon';
import { DndContext, DragEndEvent, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

interface CouponDesignerContainerProps {
    initialDesign?: CouponDesign;
    onSave?: (design: CouponDesign) => void;
    onChange?: (design: CouponDesign) => void;
    couponId?: string;
}

const defaultDesign: CouponDesign = {
    name: 'New Design',
    canvas: {
        backgroundColor: '#ffffff',
        breakpoints: {
            desktop: { width: 800, height: 600 },
            tablet: { width: 600, height: 450 },
            mobile: { width: 400, height: 300 }
        }
    },
    elements: [],
    assets: []
};

export const CouponDesignerContainer: React.FC<CouponDesignerContainerProps> = ({
    initialDesign,
    onSave,
    onChange,
    couponId
}) => {
    const [design, setDesign] = useState<CouponDesign>(initialDesign || defaultDesign);
    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>('desktop');
    const [activeTab, setActiveTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [gridSettings, setGridSettings] = useState<GridSettings>({
        enabled: true,
        size: 15,
        snap: false,
        show: false
    });

    const { isAuthenticated } = useAuth();

    // Effect to handle initialDesign updates and migrate existing elements
    useEffect(() => {
        if (initialDesign) {
            // Migrate existing elements to include name property
            const migratedElements = initialDesign.elements.map(element => {
                if (!element.name) {
                    const existingElements = initialDesign.elements.filter(e => e.type === element.type);
                    const count = existingElements.indexOf(element) + 1;
                    return {
                        ...element,
                        name: `${element.type.charAt(0).toUpperCase() + element.type.slice(1)} ${count}`
                    };
                }
                return element;
            });

            setDesign({
                ...initialDesign,
                elements: migratedElements
            });
        }
    }, [initialDesign]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, delta } = event;
        if (!active) return;

        const element = design.elements.find(e => e.id === active.id);
        if (!element) return;

        const currentPos = element.responsive[currentBreakpoint];
        const newX = currentPos.x + delta.x;
        const newY = currentPos.y + delta.y;

        // Snap to grid if enabled
        const snappedX = gridSettings.snap ? Math.round(newX / gridSettings.size) * gridSettings.size : newX;
        const snappedY = gridSettings.snap ? Math.round(newY / gridSettings.size) * gridSettings.size : newY;

        const updatedElement = {
            ...element,
            responsive: {
                ...element.responsive,
                [currentBreakpoint]: {
                    ...currentPos,
                    x: snappedX,
                    y: snappedY
                }
            }
        };

        handleElementUpdate(updatedElement);
    };

    const getDefaultContent = (type: ElementType): string => {
        switch (type) {
            case 'text':
                return 'New Text';
            case 'image':
                return 'https://placeholder.com/300x200';
            case 'qrcode':
                return 'https://example.com';
            case 'dynamic':
                return 'Dynamic Content';
            case 'divider':
                return '';
            default:
                return '';
        }
    };

    const getDefaultName = (type: ElementType): string => {
        const existingElements = design.elements.filter(e => e.type === type);
        const count = existingElements.length + 1;
        switch (type) {
            case 'text':
                return `Text ${count}`;
            case 'image':
                return `Image ${count}`;
            case 'qrcode':
                return `QR Code ${count}`;
            case 'dynamic':
                return `Dynamic Field ${count}`;
            case 'divider':
                return `Divider ${count}`;
            default:
                return `Element ${count}`;
        }
    };

    const handleAddElement = (type: ElementType) => {
        const newElement: DesignElement = {
            id: uuidv4(),
            type,
            name: getDefaultName(type),
            content: getDefaultContent(type),
            style: {
                fontSize: 16,
                fontFamily: 'Arial',
                fontWeight: 400,
                color: '#000000',
                backgroundColor: type === 'divider' ? '#000000' : 'transparent',
                textAlign: 'left',
                visibility: 'visible',
                pointerEvents: 'auto'
            },
            responsive: {
                desktop: { x: 0, y: 0, width: 200, height: type === 'divider' ? 2 : 50, rotation: 0 },
                tablet: { x: 0, y: 0, width: 150, height: type === 'divider' ? 2 : 40, rotation: 0 },
                mobile: { x: 0, y: 0, width: 100, height: type === 'divider' ? 2 : 30, rotation: 0 }
            }
        };

        setDesign(prev => ({
            ...prev,
            elements: [...prev.elements, newElement]
        }));
        setSelectedElementId(newElement.id);
    };

    const handleDesignUpdate = (updatedDesign: CouponDesign) => {
        setDesign(updatedDesign);
        // Notify parent of changes but don't trigger save
        if (onChange) {
            onChange(updatedDesign);
        }
    };

    const handleElementUpdate = (updatedElement: DesignElement) => {
        const updatedDesign = updatedElement.style?.visibility === 'hidden'
            ? {
                ...design,
                elements: design.elements.filter(e => e.id !== updatedElement.id)
            }
            : {
                ...design,
                elements: design.elements.map(el => 
                    el.id === updatedElement.id ? updatedElement : el
                )
            };

        handleDesignUpdate(updatedDesign);
    };

    const handleElementsUpdate = (updatedElements: DesignElement[]) => {
        const updatedDesign = {
            ...design,
            elements: updatedElements
        };

        handleDesignUpdate(updatedDesign);
    };

    const handleSave = async () => {
        if (!isAuthenticated) return;
        setIsSaving(true);
        try {
            // Direct API call
            await api.patch(`/admin/coupons/${couponId}`, {
                design
            });
            // Notify parent
            if (onSave) {
                onSave(design);
            }
        } catch (error) {
            console.error('Error saving design:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const selectedElement = selectedElementId 
        ? design.elements.find(e => e.id === selectedElementId) 
        : undefined;

    return (
        <DndContext 
            sensors={sensors}
            onDragEnd={handleDragEnd}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%', 
                bgcolor: 'background.default' 
            }}>
                <Button
                    id="save-design-button"
                    sx={{ display: 'none' }}
                    onClick={handleSave}
                />

                {/* Main Content */}
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Left Panel - Elements and Global Settings */}
                    <Box sx={{ width: 240, borderRight: 1, borderColor: 'divider' }}>
                        <DesignerToolbar
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            onAddElement={handleAddElement}
                            selectedElement={selectedElement}
                            onElementUpdate={handleElementUpdate}
                            design={design}
                            onDesignUpdate={handleDesignUpdate}
                            gridSettings={gridSettings}
                            onGridSettingsUpdate={setGridSettings}
                        />
                        {activeTab === 0 ? (
                            <GlobalSettings
                                design={design}
                                onDesignUpdate={handleDesignUpdate}
                                gridSettings={gridSettings}
                                onGridSettingsUpdate={setGridSettings}
                            />
                        ) : null}
                    </Box>

                    {/* Center - Canvas */}
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <DesignerCanvas
                            design={design}
                            onDesignChange={handleDesignUpdate}
                            currentBreakpoint={currentBreakpoint}
                            selectedElementId={selectedElementId}
                            onElementSelect={setSelectedElementId}
                            onElementUpdate={handleElementUpdate}
                            onElementsUpdate={handleElementsUpdate}
                            gridSettings={gridSettings}
                            couponId={couponId}
                        />
                    </Box>

                    {/* Right Panel - Layers */}
                    <Box sx={{ width: 240, borderLeft: 1, borderColor: 'divider' }}>
                        <LayersPanel
                            elements={design.elements}
                            selectedElementId={selectedElementId}
                            onElementSelect={setSelectedElementId}
                            onElementUpdate={handleElementUpdate}
                            onElementsUpdate={handleElementsUpdate}
                        />
                        {selectedElement && (
                            <ElementProperties
                                element={selectedElement}
                                onUpdate={handleElementUpdate}
                                currentBreakpoint={currentBreakpoint}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </DndContext>
    );
};

export default CouponDesignerContainer;
